import Lenis from 'lenis';

export const useAbout = (wrapper, content) => {
  const { $gsap: gsap, $SplitText, $mq, $Lenis } = useNuxtApp();

  const isModalVisible = useAboutModal();
  const isAnimating = ref(false);
  const lenisInstance = ref(null);
  const scrollTop = ref(0);
  const scrollProgress = ref(0);

  let ctx;
  let eventListeners = [];
  let delayedCalls = [];

  const initLenis = () => {
    lenisInstance.value = new Lenis({
      autoRaf: true,
      wrapper: wrapper.value,
      content: content.value,
      eventsTarget: wrapper.value,
    });
  };

  const watchScrollProgress = () => {
    lenisInstance.value.on('scroll', e => {
      scrollTop.value = e.scroll;
      scrollProgress.value = e.progress;
    });
  };

  const animateIn = () => {
    if (isAnimating.value) return;
    isAnimating.value = true;

    // Stop global Lenis to prevent conflicts and improve performance
    $Lenis?.stop();

    const dc = gsap.delayedCall(0.5, () => {
      wrapper.value?.classList.add('is-animated');
    });
    delayedCalls.push(dc);
    gsap
      .timeline()
      .fromTo(
        wrapper.value,
        {},
        {
          duration: 1.25,
          ease: 'smooth',
          force3D: true,
          onComplete: () => {
            initLenis();
            watchScrollProgress();
            isAnimating.value = false;
          },
        }
      )
      .to(
        content.value,
        {
          marginTop: $mq.isMobile ? '30vh' : '18.5vh',
          duration: 1.25,
          ease: 'smooth',
          force3D: true,
        },
        '<'
      );
  };

  const animateOut = () => {
    lenisInstance.value?.destroy();
    if (isAnimating.value) return;
    isAnimating.value = true;
    wrapper.value.classList.add('is-animated-out');

    gsap.to('#about-close', {
      y: '200%',
      duration: 0.5,
      scale: 0.75,
      ease: 'smooth',
      force3D: true,
    });

    gsap
      .timeline({
        onComplete: () => {
          isModalVisible.value = false;
          isAnimating.value = false;
          // Restart global Lenis
          $Lenis?.start();
        },
      })
      .to(wrapper.value, {
        background: 'rgba(0,0,0,0)',
        duration: 1.25,
        ease: 'expo.out',
        force3D: true,
      })
      .to(
        '.about__modal__inner',
        { opacity: 0.3, duration: 0.75, ease: 'expo.out' },
        '<'
      )
      .to(
        content.value,
        {
          marginTop: `${window.innerHeight + scrollTop.value + gsap.getProperty(content.value, `bottom`)}px`,
          duration: 1.25,
          ease: 'expo.out',
          force3D: true,
        },
        '<'
      );
  };

  const watchTextAnimationComplete = () => {
    let items = qsa('[data-split-context]', content.value);
    items.forEach(el => {
      let firstLine = qs('[data-split] .line', el);
      const handler = () => {
        el.classList.add('is-context-animating');
      };

      if (firstLine) {
        firstLine.addEventListener('transitionstart', handler, { once: true });
        eventListeners.push({
          element: firstLine,
          event: 'transitionstart',
          handler,
        });
      }
    });
  };

  const initSplitText = () => {
    ctx = gsap.context(() => {
      animateIn();
      $SplitText.create(qsa('[data-split]', content.value), {
        type: 'lines',
        mask: 'lines',
        autoSplit: true,
        linesClass: 'line',
        wordsClass: 'word',
        smartWrap: true,
        onSplit: self => {
          self?.lines?.forEach((myLine, i) => {
            gsap.set(myLine, { '--index': i });
          });

          watchTextAnimationComplete();
        },
      });
    }, wrapper.value);
  };

  const cleanup = () => {
    // Remove event listeners
    eventListeners.forEach(({ element, event, handler }) => {
      element?.removeEventListener(event, handler);
    });
    eventListeners = [];

    // Kill delayed calls
    delayedCalls.forEach(dc => dc?.kill());
    delayedCalls = [];

    ctx?.revert();
    lenisInstance.value?.destroy();

    // Ensure global Lenis is restarted
    $Lenis?.start();
  };

  return {
    scrollProgress,
    animateOut,
    initSplitText,
    cleanup,
  };
};
